import React, { useState, useEffect, useRef } from "react";
import "./ConditionCard.css";
import { ATTRIBUTE_OPTIONS, RULE_OPTIONS } from "./conditions";
import { AttributeType, RuleType, PersonaCondition } from "winslow-core";

export interface SelectFieldProps {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: { id: string; label: string }[];
}

const SelectField: React.FC<SelectFieldProps> = ({
  label,
  value,
  onChange,
  options,
}) => {
  return (
    <div className="condition-card-select-wrapper">
      <label className="condition-card-select-label">{label}</label>
      <div className="condition-card-select-container">
        <select
          className="condition-card-select-input"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}>
          <option value="">Select</option>
          {options.map((option) => (
            <option key={option.id} value={option.id}>
              {option.label}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
};

export interface ConditionCardProps {
  index: number;
  initialAttribute: AttributeType | "";
  initialRule: RuleType | "";
  initialValue: string;
  onDelete: (index: number) => void;
  onChange: (condition: PersonaCondition) => void;
  employeeAttributes: Record<AttributeType, string[]> | null;
}

export const ConditionCard: React.FC<ConditionCardProps> = ({
  index,
  initialAttribute,
  initialRule,
  initialValue,
  onDelete,
  onChange,
  employeeAttributes,
}) => {
  const [attribute, setAttribute] = useState<string>(initialAttribute);
  const [rule, setRule] = useState<string>(initialRule);
  const [value, setValue] = useState<string>(initialValue);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestionsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setAttribute(initialAttribute);
    setRule(initialRule);
    setValue(initialValue);
  }, [initialAttribute, initialRule, initialValue]);

  const getAttributeValues = () => {
    if (!employeeAttributes || !attribute) return [];
    return employeeAttributes[attribute as AttributeType] || [];
  };

  const getSuggestions = () => {
    const attributeValues = getAttributeValues();
    if (!value) return attributeValues;
    return attributeValues.filter((suggestion) =>
      suggestion.toLowerCase().includes(value.toLowerCase())
    );
  };

  useEffect(() => {
    onChange({
      attribute: attribute as AttributeType,
      rule: rule as RuleType,
      value,
    });
  }, [attribute, rule, value]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        suggestionsRef.current &&
        !suggestionsRef.current.contains(event.target as Node)
      ) {
        setShowSuggestions(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <div className="condition-card-container">
      <div className="condition-card-card">
        <div className="condition-card-content">
          <div className="condition-card-selection-row">
            <SelectField
              label="Attribute"
              value={attribute}
              onChange={setAttribute}
              options={ATTRIBUTE_OPTIONS}
            />
            <SelectField
              label="Rule"
              value={rule}
              onChange={setRule}
              options={RULE_OPTIONS}
            />
          </div>
          <div className="condition-card-value-section">
            <label className="condition-card-value-label">Value</label>
            <div className="condition-card-value-container">
              <input
                type="text"
                className="condition-card-value-input"
                placeholder="Enter value..."
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onFocus={() => setShowSuggestions(true)}
                aria-label="Value input"
              />
              {showSuggestions && attribute && getSuggestions().length > 0 && (
                <div
                  className="condition-card-suggestions"
                  ref={suggestionsRef}>
                  {getSuggestions().map((suggestion, index) => (
                    <div
                      key={index}
                      className="condition-card-suggestion-item"
                      onClick={() => {
                        setValue(suggestion);
                        setShowSuggestions(false);
                      }}>
                      {suggestion}
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div
            className="condition-card-delete-button"
            aria-label="Delete condition">
            <span onClick={() => onDelete(index)}>Delete Condition</span>
          </div>
        </div>
      </div>
    </div>
  );
};
